import React, { useEffect, useState } from 'react';
import { ScrollView, View, useWindowDimensions } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import { Document, Page, pdfjs } from 'react-pdf';
import { ActivityIndicator, IconButton, Surface } from 'react-native-paper';
import { FontAwesome5 } from '@expo/vector-icons';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';

import { OrderPatientService } from '@luminate/luminate-ts-sdk';
import { SubText } from '@luminate/luminate-native-components';

import { useAuth, useThemeContext } from '../../contexts';
import Environment from '../../models/Environment';
import { Button } from '../common';
import { OrderConfirmationPdfWebScreenRouteProp, SCREEN_NAMES } from '../routes';
import { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
import 'react-pdf/dist/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.mjs`;

export const OrderConfirmationPdfWebScreen = () => {
  const { authData, displaySnack } = useAuth();
  const { theme } = useThemeContext();
  const route = useRoute<OrderConfirmationPdfWebScreenRouteProp>();
  const [pdf, setPdf] = useState<Blob>();
  const [downloading, setDownloading] = useState(false);
  const defaultScale = 1;
  const maxScale = 2;
  const [zoomScale, setZoomScale] = useState(defaultScale);
  const { width, height } = useWindowDimensions();
  const maxWidth = width < 1024 ? width : 1024;
  const [numberOfPages, setNumberOfPages] = useState<number | null>(null);
  const navigation = useNavigation();

  const loadPdf = async () => {
    try {
      const pdfBlob = await OrderPatientService.create(
        Environment.apiBaseUrl as string,
        authData?.encodedJwt as string,
      ).downloadOrder(route.params.orderId);
      setPdf(pdfBlob);
    } catch (ex) {
      showDownloadError();
    }
  };

  const showDownloadError = (_error?: Error) => {
    displaySnack(
      <SubText style={{ color: theme.colors.surface }}>
        We are unable to download the order confirmation pdf at this time. Please try again later.
      </SubText>,
    );
  };

  const renderLoading = () => {
    return <ActivityIndicator animating={true} size="large" />;
  };

  const onDownloadPdf = async () => {
    setDownloading(true);
    try {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(pdf);
      fileReader.onerror = () => {
        showDownloadError();
      };
      fileReader.onload = () => {
        const hiddenDownloadLink = document.createElement('a');
        hiddenDownloadLink.href = fileReader.result as string;
        hiddenDownloadLink.target = '_blank';
        hiddenDownloadLink.download = `${route.params.oderNumber}.pdf`;
        hiddenDownloadLink.click();
        setDownloading(false);
      };
    } catch (ex) {
      showDownloadError();
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (authData?.encodedJwt) {
      loadPdf();
    } else {
      navigation.navigate(SCREEN_NAMES.BROWSE);
    }
  }, []);

  const onPdfLoadSuccess = (pdf: PDFDocumentProxy) => {
    setNumberOfPages(pdf.numPages);
  };

  return (
    <>
      <Surface style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', padding: 5 }}>
        <View style={{ position: 'absolute', left: 5 }}>
          <IconButton
            icon={() => <MaterialIcons name="arrow-back" size={32} color={theme.colors.primary} />}
            onPress={() => navigation.goBack()}
            disabled={downloading}
          />
        </View>

        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Button
            disabled={downloading}
            loading={downloading}
            icon={() => <FontAwesome5 name="file-download" size={24} color={theme.colors.surface} />}
            onPress={onDownloadPdf}
          >
            Download
          </Button>
          <IconButton
            icon={() => <MaterialIcons name="zoom-in" size={32} color={theme.colors.primary} />}
            disabled={zoomScale > maxScale}
            onPress={() => setZoomScale(zoomScale + 0.1)}
          />
          <IconButton
            icon={() => <MaterialIcons name="zoom-out" size={32} color={theme.colors.primary} />}
            disabled={zoomScale < 0.11}
            onPress={() => setZoomScale(zoomScale - 0.1)}
          />
          <IconButton
            icon={() => <MaterialIcons name="aspect-ratio" size={32} color={theme.colors.primary} />}
            onPress={() => setZoomScale(defaultScale)}
            disabled={zoomScale === defaultScale}
          />
        </View>
      </Surface>
      <ScrollView>
        <ScrollView
          style={{ paddingTop: 5 }}
          horizontal={true}
          contentContainerStyle={{ justifyContent: 'center', width: `100%` }}
        >
          <Document
            file={pdf}
            renderMode="canvas"
            loading={renderLoading}
            noData={() => <></>}
            onLoadSuccess={onPdfLoadSuccess}
            onSourceError={showDownloadError}
          >
            {Array.from(new Array(numberOfPages)).map((_value, index) => (
              <Page
                scale={zoomScale}
                key={index}
                pageNumber={index + 1}
                renderMode={'canvas'}
                renderTextLayer={false}
                width={maxWidth * 0.9}
                height={height * 0.9}
              />
            ))}
          </Document>
        </ScrollView>
      </ScrollView>
    </>
  );
};
